import { useState, useMemo, useCallback, useRef } from 'react';
import { Eye, Globe, Star, Upload, Loader2, Camera } from 'lucide-react';
import { type PhotoTagEntityType } from '@peles/shared';
import { usePhotos, usePhotoBatchUrls } from '../../hooks/usePhotos';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { UploadProgressBar } from './UploadProgressBar';
import { GooglePhotosPicker } from './GooglePhotosPicker';
import { PhotoDetailPanel } from './PhotoDetailPanel';
import { EntityModal } from '../common/EntityModal';

interface PhotoListViewProps {
  /** Pre-filter by entity tag */
  entityType?: PhotoTagEntityType;
  entityId?: string;
  /** Max items to show (default: 12) */
  limit?: number;
  /** Show publish status filters */
  showPublishFilters?: boolean;
  /** Enable upload with auto-tag to this entity */
  enableUpload?: boolean;
  /** Current user for detail panel */
  currentUser?: { id: string; systemRole: string; name: string; email: string };
}

const PAGE_SIZE = 24;

export function PhotoListView({
  entityType,
  entityId,
  limit = 12,
  showPublishFilters = false,
  enableUpload = false,
  currentUser,
}: PhotoListViewProps) {
  const [page, setPage] = useState(1);
  const [publishFilter, setPublishFilter] = useState<'all' | 'public' | 'landing'>('all');
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = usePhotos({
    entityType,
    entityId,
    page,
    limit: PAGE_SIZE,
  });

  // Upload queue with auto-tagging
  const autoTags = entityType && entityId ? [{ entityType, entityId }] : undefined;
  const { addFiles, queue, progress, isUploading, clear } = useUploadQueue();

  const photos = data?.photos ?? [];
  const total = data?.total ?? 0;

  // Client-side publish filter
  const filtered = useMemo(() => {
    if (publishFilter === 'public') return photos.filter((p) => p.isPublic);
    if (publishFilter === 'landing') return photos.filter((p) => p.showOnLanding);
    return photos;
  }, [photos, publishFilter]);

  const displayPhotos = filtered.slice(0, limit);
  const photoIds = displayPhotos.map((p) => p.id);
  const { data: urlMap = {} } = usePhotoBatchUrls(photoIds);

  const handleUploadFiles = useCallback(
    (files: File[]) => {
      // Add files to upload queue with auto-tags
      addFiles(
        files,
        autoTags?.map((t) => ({ entityType: t.entityType, entityId: t.entityId })),
      );
    },
    [addFiles, autoTags],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleUploadFiles(Array.from(e.target.files));
        e.target.value = '';
      }
    },
    [handleUploadFiles],
  );

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <div className="inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Camera size={16} className="text-primary-600" />
          Photos
          {total > 0 && <span className="text-sm font-normal text-gray-400">({total})</span>}
        </h3>
        <div className="flex items-center gap-2">
          {showPublishFilters && (
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg overflow-hidden">
              {(['all', 'public', 'landing'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setPublishFilter(f)}
                  className={`px-2.5 py-1 text-xs transition-colors ${
                    publishFilter === f
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {f === 'all' ? (
                    'All'
                  ) : f === 'public' ? (
                    <span className="flex items-center gap-1">
                      <Globe size={10} /> Public
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Star size={10} /> Landing
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {enableUpload && (
            <>
              <GooglePhotosPicker
                onFilesSelected={(files) => handleUploadFiles(files)}
                disabled={isUploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {isUploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                Upload
              </button>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,video/mp4,video/quicktime,video/webm"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Upload progress */}
      {(isUploading || queue.length > 0) && (
        <div className="mb-3">
          <UploadProgressBar
            queue={queue}
            progress={progress}
            isUploading={isUploading}
            onClear={clear}
          />
        </div>
      )}

      {/* Grid */}
      {displayPhotos.length === 0 ? (
        <div className="py-8 text-center">
          {enableUpload ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex flex-col items-center gap-2 text-gray-400 hover:text-primary-600 transition-colors"
            >
              <Camera size={32} />
              <span className="text-sm">Click to upload photos</span>
            </button>
          ) : (
            <p className="text-sm text-gray-400">No photos found</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {displayPhotos.map((photo) => {
            const url = urlMap[photo.id];
            const isVideo = photo.mimeType.startsWith('video/');
            return (
              <div
                key={photo.id}
                onClick={() => setSelectedPhotoId(photo.id)}
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group cursor-pointer"
              >
                {url ? (
                  isVideo ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">▶</span>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={photo.caption || photo.fileName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {/* Publish badges */}
                {(photo.isPublic || photo.showOnLanding) && (
                  <div className="absolute top-1 right-1 flex gap-0.5">
                    {photo.showOnLanding && (
                      <span className="w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                        <Star size={8} className="text-white fill-white" />
                      </span>
                    )}
                    {photo.isPublic && !photo.showOnLanding && (
                      <span className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Globe size={8} className="text-white" />
                      </span>
                    )}
                  </div>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Eye className="text-white drop-shadow" size={20} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Show more */}
      {filtered.length > limit && (
        <div className="mt-3 text-center">
          <a href="/photos" className="text-xs text-primary-600 hover:text-primary-700">
            View all {total} photos →
          </a>
        </div>
      )}

      {/* Pagination for standalone mode */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-gray-200 rounded-lg disabled:opacity-30"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page * PAGE_SIZE >= total}
            className="px-3 py-1 text-sm border border-gray-200 rounded-lg disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}

      {/* Photo Detail Modal */}
      {selectedPhotoId && currentUser && (
        <EntityModal
          open={true}
          onClose={() => setSelectedPhotoId(null)}
          title="Photo Details"
          size="lg"
        >
          <PhotoDetailPanel
            photoId={selectedPhotoId}
            onClose={() => setSelectedPhotoId(null)}
            currentUser={currentUser as any}
          />
        </EntityModal>
      )}
    </div>
  );
}
